import React, { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { nanoid } from 'nanoid';
import classNames from 'classnames';
import { componentSizeVariantsEnum } from 'src/constants/common.constants';
import { useGetFormikField } from 'src/hooks';

import './inputfield2.scss';

import ErrorMessage from '../errorMessage/ErrorMessage';
import InputElement2 from './InputElement2';
import Label2 from './Label2';

type TInputField2Props = {
  errorMessage?: string;
  label?: ReactNode;
  id: string;
  variant?: componentSizeVariantsEnum;
  startIcon?: React.ReactNode;
  removable?: boolean;
  onRemove?: () => void;
  endIcon?: React.ReactNode;
  startIconClickHandler?: () => void;
  endIconClickHandler?: () => void;
  maskField?: boolean;
  elementtype?: string;
  inputFieldClassName?: string;
  isTouched?: boolean;
  inputRef?: (el: HTMLInputElement) => void;
  required?: boolean;
  component?: React.ReactNode
} & InputHTMLAttributes<HTMLInputElement>;

/**
 * Props for the InputField2 component - Version 2 Modern Design.
 * @typedef {Object} TInputField2Props
 * @param {string} [errorMessage] - The error message to be displayed.
 * @param {string} label - The label for the input field.
 * @param {string} [id] - The ID attribute for the input field.
 * @param {componentSizeVariantsEnum} [variant] - The variant of the input field.
 * @param {React.ReactNode} [startIcon] - The icon element to be displayed at the start.
 * @param {React.ReactNode;} [endIcon] - The icon element to be displayed at the end.
 * @param {string} [inputFieldClassName] - Additional class name(s) for the input field.
 * @param {() = void} [startIconClickHandler] - Click handler for the left icon.
 * @param {() = void} [endIconClickHandler] - Click handler for the right icon.
 * @param {boolean} [markField] - The icon element to be displayed at the end.
 * @param {boolean} [isTouched] - Whether the field is touched.
 * @param {InputHTMLAttributes<HTMLInputElement>} [HTMLInputElement] - HTML attributes of the input element.
 */
const InputField2 = ({
  errorMessage,
  label,
  id,
  variant = componentSizeVariantsEnum.SMALL,
  startIcon,
  endIcon,
  component = <></>,
  inputFieldClassName,
  className,
  isTouched,
  required = false,
  value,
  removable = false,
  onRemove = () => { },
  elementtype = 'other',
  onChange,
  startIconClickHandler,
  endIconClickHandler,
  ...rest
}: TInputField2Props) => {
  // Creates unique id, if not available
  const inputId: string = id || nanoid();

  const { getFormikField } = useGetFormikField();

  const [field, fieldMetaData, helpers] = getFormikField({ id: id || '' });

  // The below code is written such that InputField2 works even when it is not in Formik
  // Component can still be used independently outside Formik
  const error: string = errorMessage || fieldMetaData?.error || '';
  const fieldTouched: boolean = isTouched || fieldMetaData?.touched;
  const fieldValue = elementtype === 'program-creation' ? value : field?.value || value;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      // When InputField2 is not wrapped in Formik
      onChange(e);
    } else if (helpers?.setValue) {
      // When InputField2 is wrapped in Formik
      helpers.setValue(e.target.value);
    }
  };

  const handleStartIconClick = () => {
    if (startIconClickHandler) {
      startIconClickHandler();
    }
  };

  const handleEndIconClick = () => {
    if (endIconClickHandler) {
      endIconClickHandler();
    }
  };

  return (
    <div
      className={classNames(
        className,
        'input-field-component-v2',
        `input-field-component-v2--nogap`
      )}
    >
      {label && (
        <div className='input-field-component-v2__labelBox'>
          <Label2 htmlFor={inputId} variant={variant} required={required}>
            <span>{label}</span>
          </Label2>
          {component}
          {removable && (
            <span 
              className='input-field-component-v2__labelBox__remove-button' 
              onClick={() => onRemove && onRemove()}
            >
              Remove
            </span>
          )}
        </div>
      )}

      <InputElement2
        {...rest}
        variant={variant}
        startIcon={startIcon}
        endIcon={endIcon}
        className={inputFieldClassName}
        id={inputId}
        onChange={handleChange}
        value={fieldValue}
        onStartIconClick={handleStartIconClick}
        onEndIconClick={handleEndIconClick}
        hasError={!!(error && fieldTouched)}
      />
      
      {error && typeof error === 'string' && fieldTouched && (
        <ErrorMessage message={error} />
      )}
    </div>
  );
};

export default InputField2;
